import { LinearProgress, MenuItem, useMediaQuery, useTheme, Badge } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { Stack } from '@mui/system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { UserButton, useIsMobile, IconButton } from '@tix-ticketing/ui-components';
import { CartIcon } from '@tix-ticketing/ui-icons/outline';
import { hasDarkScheme, headerBarStyles } from '@tix-ticketing/ui-theme';
import { useRouter } from 'next/router';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { postApiAuthLogout } from 'api/generated';
import { getApiCurrenciesRatesOptions } from 'api/generated/@tanstack/react-query.gen';
import { LocaleLink } from 'components/common/locale-link';
import { AppConfigSelector } from 'components/common/navbar/app-config-selector';
import { OrderTimer } from 'components/common/navbar/order-timer';
import { AccountHeader, useNavbarLinks } from 'components/common/page-header/account-header';
import { LanguageSelector } from 'components/common/selectors/language-selector';
import { useAppState } from 'lib/app-state';
import { useDedicatedOrderRoute } from 'lib/dedicated-order';
import { getOrderItemCount } from 'lib/helpers/order-helpers';
import { getNameInitials } from 'lib/helpers/string-helpers';
import { useAuthSession } from 'lib/hooks/use-auth-session';
import { useAvailableLocales } from 'lib/hooks/use-available-locales';
import { useReservationKeptOrder } from 'lib/hooks/use-reservation-removals';
import { useSignOut } from 'lib/hooks/use-sign-out';
import { staletime } from 'lib/tanstack-query-client';
import { useThemeConfig } from 'lib/theme-config';

// Renders navbar children and reports their measured height so the main
// content below the fixed navbar can be offset by the right amount.
function NavbarMeasuredSlot({
  children,
  onHeightChange
}: {
  children: React.ReactNode;
  onHeightChange: (height: number) => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const hasChildren = !!children;
  React.useEffect(() => {
    if (!ref.current || !hasChildren) {
      onHeightChange(0);
      return;
    }
    const el = ref.current;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        onHeightChange(entry.contentRect.height);
      }
    });
    observer.observe(el);
    onHeightChange(el.offsetHeight);
    return () => {
      observer.disconnect();
      onHeightChange(0);
    };
  }, [onHeightChange, hasChildren]);
  if (!children) return null;
  return <Box ref={ref}>{children}</Box>;
}
function ElevationScroll({
  children,
  enabled
}: {
  enabled: boolean;
  children: React.ReactElement<any>;
}) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0
  });
  return React.cloneElement(children, {
    elevation: enabled ? trigger ? 3 : 0 : 3
  });
}
export const headerHeight = 96;
export const headerHeightSmall = 64;
export const Navbar = () => {
  const intl = useIntl();
  const themeConfig = useThemeConfig();
  const {
    appSettings,
    eventData: {
      order
    },
    locale,
    setLocale,
    currency,
    setCurrency,
    title,
    progress,
    navbarContent,
    setNavbarExtraHeight,
    banner,
    setNavbarBannerHeight,
    setIsCartOpen,
    isCartOpen
  } = useAppState();
  // What the cart is showing: items removed from a reservation are off the
  // cart, so the badge must not go on counting them.
  const {
    keptOrder
  } = useReservationKeptOrder();
  // A reservation or renewal flow is paid on its own, so the buyer's cart is
  // out of reach until they leave it.
  const isDedicatedOrderFlow = !!useDedicatedOrderRoute();
  const signOut = useSignOut();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const accountPageMatches = useMediaQuery(theme.breakpoints.up('lg'));
  const navbarLinks = useNavbarLinks();
  const router = useRouter();
  const {
    pathname
  } = router;
  const {
    isAuthenticated,
    isResolving: isAuthResolving,
    profile
  } = useAuthSession();
  const isAccountPage = pathname.includes('/account');
  // The account tabs link to pages that only exist for a signed-in customer;
  // signed out, an account page is the sign-in form, so the tabs stay hidden.
  const showAccountPageHeader = isAccountPage && accountPageMatches && isAuthenticated;
  const locales = useAvailableLocales();

  // The bar is only rendered while `progress !== null && !isAccountPage` (see
  // below) — so it's freshly mounting, with nothing to grow from, whenever
  // that combined visibility flips from hidden to shown, not only when
  // `progress` itself moves from `null`. Account pages never call
  // `setProgress` at all, so `progress` can easily still hold some leftover
  // non-null value from whatever set it last while the bar sat hidden there
  // — keying only off `progress` would miss that case and still blink.
  // Painting at 0 first and bumping to the real value two frames later gives
  // the bar something to animate from on that transition; every other
  // change (progress moving between two non-null values while already
  // visible, e.g. between two steps of the same flow) already has a real
  // value in the DOM to transition from, so it's mirrored immediately and
  // picked up by `LinearProgress`'s own transition. Lives here — once, where
  // the bar actually renders — rather than duplicated in every page that
  // calls `setProgress`.
  //
  // A single `requestAnimationFrame` isn't enough: nothing guarantees the
  // browser paints the `0` state before that callback fires, so React can
  // coalesce both updates into one commit, which still blinks straight to
  // the target. Nesting a second `requestAnimationFrame` inside the first is
  // the standard "wait two frames" trick — the outer callback only runs once
  // the browser is ready to paint the frame after the `0` commit, so
  // scheduling the real update from inside it guarantees a `0` paint first.
  const isVisible = progress !== null && !isAccountPage;
  // Cleared to `null` while hidden, so a bar re-entering view never mounts
  // at a stale value before the entry animation resets it to 0.
  const [displayProgress, setDisplayProgress] = React.useState(isVisible ? progress : null);
  const wasVisibleRef = React.useRef(isVisible);
  React.useEffect(() => {
    const wasVisible = wasVisibleRef.current;
    wasVisibleRef.current = isVisible;
    if (!wasVisible && isVisible) {
      setDisplayProgress(0);
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setDisplayProgress(progress));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    setDisplayProgress(isVisible ? progress : null);
  }, [progress, isVisible]);
  const currencyQuery = useQuery({
    ...getApiCurrenciesRatesOptions(),
    staleTime: staletime.MEDIUM
  });

  // Filter currencies to only those available in app settings
  const currencies = (() => {
    const rates = currencyQuery?.data;
    if (!rates || !appSettings?.availableCurrencies?.length) return rates ?? {};
    const allowedKeys = new Set(appSettings.availableCurrencies.map(c => c.currencyKey));
    return {
      ...rates,
      currencyRates: rates.currencyRates?.filter((r: {
        currencyKey?: string | null;
      }) => allowedKeys.has(r.currencyKey))
    };
  })();
  const getMeInitials = () => {
    if (!profile) return '';
    return getNameInitials(profile?.webCustomer?.attributes?.find((i: any) => i.name === 'Name')?.value ?? '');
  };
  const logoutMutation = useMutation({
    mutationFn: () => postApiAuthLogout(),
    onSettled: () => {
      signOut();
      // Account pages would otherwise re-render into the sign-in form under
      // the customer, which reads as an error rather than a sign-out.
      if (router.pathname.startsWith('/account')) {
        router.push('/');
      }
    }
  });
  return <Box sx={{
    display: 'flex'
  }}>
      <ElevationScroll enabled={progress === null && !navbarContent}>
        <AppBar component="nav" color="inherit" position="fixed" sx={headerBarStyles(theme.palette)}>
          <Toolbar sx={[{
          height: {
            xs: headerHeightSmall,
            sm: headerHeight
          },
          maxHeight: {
            xs: headerHeightSmall,
            sm: headerHeight
          },
          justifyContent: 'space-between',
          alignItems: 'center',
          gridTemplateColumns: '1fr auto 1fr',
          gap: 1
        }, showAccountPageHeader ? {
          display: 'grid'
        } : {
          display: 'flex'
        }]}>
            {/* Logo and Title */}
            <Stack sx={{
            flexDirection: 'row',
            alignItems: 'center'
          }}>
              {/* Logo */}
              <LocaleLink href="/">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                minWidth: '69px',
                height: {
                  xs: '32px',
                  sm: '40px'
                }
              }} aria-label="Home" title="Home">
                  <img src={(theme.palette.mode === 'dark' && hasDarkScheme(themeConfig) && themeConfig.darkTheme?.logoUrl ? themeConfig.darkTheme.logoUrl : themeConfig.theme.logoUrl) ?? ''} alt={themeConfig.theme.logoAlt ?? intl.formatMessage({
                  id: "navbar.logoAlt",
                  defaultMessage: [{
                    "type": 0,
                    "value": "Logo"
                  }]
                })} style={{
                  maxHeight: '100%',
                  maxWidth: '100%'
                }} />
                </Box>
              </LocaleLink>

              {/* Step Title */}
              <Typography variant="body2" component="div" sx={{
              flexGrow: 1,
              display: {
                xs: 'none',
                sm: 'block'
              },
              ml: 5,
              fontSize: 16,
              fontWeight: 700
            }}>
                {title}
              </Typography>
            </Stack>

            {showAccountPageHeader && <AccountHeader />}

            <Stack sx={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1
          }}>
              {process.env.NEXT_PUBLIC_DEV_QOL === 'true' && <AppConfigSelector />}
              {/* Language Button */}
              <Box sx={{
              alignSelf: 'center'
            }}>
                <LanguageSelector locales={locales} currencies={currencies} onLocaleChange={setLocale} onCurrencyChange={setCurrency} selectedLocale={locale} selectedCurrency={currency} />
              </Box>
              {/* Timer */}
              <OrderTimer />
              {/* Cart Button */}
              {order && !isDedicatedOrderFlow && <IconButton data-testid="mini-cart-button" color="monochrome" variant={isMobile ? 'text' : 'outlined'} size="medium" onClick={() => setIsCartOpen(!isCartOpen)} sx={{
              mr: {
                xs: -0.5,
                sm: 1.5
              }
            }} label={intl.formatMessage({
              id: "cart.title",
              defaultMessage: [{
                "type": 0,
                "value": "Your cart"
              }]
            })}>
                  <>
                    <CartIcon />
                    <Badge badgeContent={getOrderItemCount(keptOrder)} color="primary" sx={{
                  '& .MuiBadge-badge': {
                    transform: isMobile ? 'scale(1) translate(75%, -100%)' : 'scale(1) translate(100%, -100%)'
                  }
                }} />
                  </>
                </IconButton>}

              {/* User Button or Sign in */}
              {isAuthResolving ?
            // Neither control is correct until the session is known: a
            // Sign In button under a signed-in customer reads as a dropped
            // login. Hold the same button in its loading state so nothing
            // shifts once the answer arrives.
            //
            // Deliberately does not cover `isUnavailable`: once we have
            // given up asking, a permanently loading button would be the
            // same forever-spinner in miniature. That case falls through to
            // the ordinary Sign In button below — a wrong guess in global
            // chrome is cosmetic and clicking it lands on /account, which
            // explains what went wrong.
            <Button variant="outlined" color="monochrome" size={'medium'} loading sx={{
              ml: {
                xs: 1,
                sm: 0
              }
            }}>
                  <FormattedMessage id="common.signIn" defaultMessage={[{
                "type": 0,
                "value": "Sign In"
              }]} />
                </Button> : isAuthenticated ? <UserButton initials={getMeInitials()} ariaLabel={intl.formatMessage({
              id: "account.menu",
              defaultMessage: [{
                "type": 0,
                "value": "Account menu"
              }]
            })} name={profile?.webCustomer?.attributes?.find((i: any) => i.name === 'Name')?.value ?? ''} email={profile?.webCustomer?.attributes?.find((i: any) => i.name === 'Email')?.value ?? ''} signOutLabel={<FormattedMessage id="account.signOut" defaultMessage={[{
              "type": 0,
              "value": "Sign out"
            }]} />} onSignOut={() => logoutMutation.mutate()} actions={navbarLinks.map(link => <MenuItem onClick={() => router.push(link.href)} key={link.href}>
                      {link.icon}
                      {link.title}
                    </MenuItem>)} /> : <Button variant="outlined" color="monochrome" size={'medium'} onClick={() => router.push('/account')} sx={{
              ml: {
                xs: 1,
                sm: 0
              }
            }}>
                  <FormattedMessage id="common.signIn" defaultMessage={[{
                "type": 0,
                "value": "Sign In"
              }]} />
                </Button>}
            </Stack>
          </Toolbar>
          {/* Navbar content slot (e.g. event card on mobile, subscription event tabs) */}
          <NavbarMeasuredSlot onHeightChange={setNavbarExtraHeight}>
            {navbarContent}
          </NavbarMeasuredSlot>
          {/* Full-width banner pinned to the bottom of the navbar (e.g.
              cancelled-event notice). Stays visible while the fixed navbar is
              on screen. */}
          <NavbarMeasuredSlot onHeightChange={setNavbarBannerHeight}>
            {banner}
          </NavbarMeasuredSlot>
          {/* Progress Bar */}
          {displayProgress !== null && !isAccountPage ? <Box sx={{
          width: '100%',
          position: 'absolute',
          bottom: '-4px'
        }}>
              <LinearProgress variant="determinate" value={displayProgress} sx={{
            '&.MuiLinearProgress-root': {
              backgroundColor: 'transparent'
            }
          }} />
            </Box> : null}
        </AppBar>
      </ElevationScroll>
    </Box>;
};